/**
 * Time filtering utilities for BreakdownCell[] data
 * Filters cells based on time filter presets
 */

import type { BreakdownCell } from '@/components/ui/breakdown-tables/types';
import { TimeFilterPreset } from '@/types/rebateAnalytics';
import { timeFilterPresetToSelectedMonths } from '../timeFilterPresets';
import { resolveMonth } from '@/components/rebates/MonthPicker';

/**
 * Sort dates chronologically
 * Handles both Date objects and date strings (YYYY-MM-DD format)
 */
export function sortDatesChronologically<T extends Date | string>(
  dates: T[],
): T[] {
  return [...dates].sort((a, b) => {
    const dateA: Date = typeof a === 'string' ? new Date(a) : a;
    const dateB: Date = typeof b === 'string' ? new Date(b) : b;
    return dateA.getTime() - dateB.getTime();
  });
}

/**
 * Parse month from column name
 * Handles formats: "Jan", "Jan 2025", "Jan - Supplier A", "Jan 2025 - Supplier A"
 */
function parseMonthFromColumn(column: string): string | null {
  const parts = column.split(' - ');
  const monthPart = parts[0].trim();

  // Extract month (remove year if present)
  const monthMatch = monthPart.match(
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i,
  );
  if (monthMatch) {
    // Capitalize first letter, lowercase rest
    const month = monthMatch[1];
    return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
  }

  return null;
}

/**
 * Filter BreakdownCell[] by time filter preset
 */
export function filterByTimePreset({
  cells,
  timeFilterPreset,
}: {
  cells: BreakdownCell[];
  timeFilterPreset: TimeFilterPreset;
}): BreakdownCell[] {
  if (timeFilterPreset === 'all') {
    return cells; // No filtering needed
  }

  // Get selected months from preset
  const selectedMonths = timeFilterPresetToSelectedMonths(timeFilterPreset);
  if (!selectedMonths || selectedMonths.length === 0) {
    return cells; // No months selected, return all
  }

  // Resolve month names (handle "Current", "Previous Month", etc.)
  const resolvedMonths = selectedMonths.map(resolveMonth);

  // Filter cells by column month
  return cells.filter((cell) => {
    const month = parseMonthFromColumn(cell.column);
    if (!month) {
      // Can't parse month, include it (might be a custom column or total)
      return true;
    }
    return resolvedMonths.includes(month);
  });
}
